import markdownParser from 'marked';
import { MarkdownHTMLOverride, MarkdownHTMLOverrides } from './markdown.types';
import React from 'react';
import ReactDOMServer from 'react-dom/server';

// Regex Tester https://regexr.com/5qaq4 | Extracts individual attributes from a full string of all attributes in an HTML tag
const REGEX_EXTRACT_ATTRS = /([\S]+)=(?:["|'])([^'|"]+)(?:["|'])/gim;
// Regex Tester https://regexr.com/5qj4o | Extracts key and value from an attribute string (href="www.mlb.com" -> href: "www.mlb.com")
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const REGEX_ATTRS_KEY_VALUE = /(?<key>[\S]+)=["|'](?<value>[^"|']+)/im;

/**
 * Generate replacement string for matched HTML tag
 * @param Component - React Element replacement for HTML Entity
 */
const replacer = (currentOverride: MarkdownHTMLOverride) => {
  const { component: Component, props } = currentOverride;

  return (...args: RegExpMatchArray) => {
    /* Captured Groups Index: 0 = full match, 1 = tag, 2 = attributes, 3: children */

    const attributes = args[2];

    const children = args[3];

    if (!Component) {
      return children;
    }

    const acc: { [key: string]: string } = {};
    const attributesToProps = attributes?.match(REGEX_EXTRACT_ATTRS)?.reduce((arr = {}, val) => {
      const attr = val.match(REGEX_ATTRS_KEY_VALUE);

      if (attr?.groups) {
        const { key, value } = attr.groups;
        arr[key] = value;
      }
      return arr;
    }, acc);

    const mergedProps = {
      ...attributesToProps,
      ...props,
    };

    return ReactDOMServer.renderToString(<Component {...mergedProps}>{children}</Component>);
  };
};

/**
 * Itereate through  overrides object and initiate replacements
 * @param md
 * @param overrides
 */
const overrideHtmlTagsInMd = (md: string, overrides: MarkdownHTMLOverrides) => {
  let modifiedMd = md;

  for (const tagName in overrides) {
    if (Object.prototype.hasOwnProperty.call(overrides, tagName)) {
      const currentOverride = overrides[tagName];

      if (!currentOverride || !currentOverride.component) {
        return modifiedMd;
      }

      const isSelfClosing = currentOverride?.isSelfClosing;

      /* RegEx to find all the tags in the md, matching current override key (eg. forge-entity, a, div)
       * This will capture any similar nested tags as well, (<span><span>text</span></span>)
       * Capture Groups: 0 = full match, 1 = tag, 2 = attributes, 3: children */
      const htmlTags = new RegExp(
        `<(${tagName}[^ >/]*) ?([^>]*)/{0}>\\n?(\\s*(?:<\\1[^>]*?>[\\s\\S]*?</\\1>|(?!<\\1)[\\s\\S])*?)</\\1>`,
        'gmi',
      );
      const htmlTagsSelfClosing = new RegExp(`<(${tagName})([^<>/]*)[>/]>?`, 'gmi');

      const htmlTagRegEx = isSelfClosing ? htmlTagsSelfClosing : htmlTags;
      modifiedMd = modifiedMd.replace(htmlTagRegEx, replacer(currentOverride));
    }
  }

  return modifiedMd;
};

/**
 * Parse Markdown string to HTML
 * Override any existing html entity in md based on overrides passed in
 * @param md (string) Markdown string
 * @param overrides (object) overrides to override any html entity in markdown
 */
export const parseMarkdown = (md: string, overrides?: MarkdownHTMLOverrides) => {
  if (!md || typeof md !== 'string') {
    return '';
  }

  if (overrides && Object.keys(overrides).length) {
    const parsedMarkdown = markdownParser(md);
    return overrideHtmlTagsInMd(parsedMarkdown, overrides);
  }

  return markdownParser(md);
};
